import { Alert, AlertDescription } from "@repo/ui/alert";
import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@repo/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@repo/ui/table";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, ExternalLink, KeyRound, Loader2, RefreshCw } from "lucide-react";
import {
  checkValuationDataSource,
  fetchValuationDataSources,
  type ValuationDataSourceRecord,
} from "@/lib/api";

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  planned: "secondary",
  pending_contract: "outline",
  disabled: "secondary",
  error: "destructive",
};

const CHECK_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  ok: "default",
  warning: "outline",
  error: "destructive",
  needs_secret: "secondary",
  skipped: "secondary",
};

function formatLabel(value: string | null | undefined): string {
  return value ? value.replace(/_/g, " ") : "—";
}

function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "Never";
  }
  return new Intl.DateTimeFormat("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function sourceSummary(sources: ValuationDataSourceRecord[]) {
  return {
    active: sources.filter((source) => source.status === "active").length,
    api: sources.filter((source) => source.access_mode === "api").length,
    ingress: sources.filter((source) => source.supports_ingress).length,
    secretBacked: sources.filter((source) => source.auth_required).length,
  };
}

const DataConnections = () => {
  const queryClient = useQueryClient();
  const {
    data: sources = [],
    error,
    isLoading,
  } = useQuery({
    queryKey: ["valuation-data-sources"],
    queryFn: fetchValuationDataSources,
  });

  const checkMutation = useMutation({
    mutationFn: checkValuationDataSource,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["valuation-data-sources"] });
    },
  });
  const summary = sourceSummary(sources);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Data Connections</h1>
          <p className="text-muted-foreground">
            Registry of external and internal data sources that can feed PV algorithms.
          </p>
        </div>
        <Badge variant="outline" className="w-fit">
          PV inputs
        </Badge>
      </div>

      {error ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error
              ? error.message
              : "Unable to load valuation data connections."}
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active sources</CardDescription>
            <CardTitle>{summary.active}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>API connections</CardDescription>
            <CardTitle>{summary.api}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Ingress capable</CardDescription>
            <CardTitle>{summary.ingress}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Require secrets</CardDescription>
            <CardTitle>{summary.secretBacked}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Source registry</CardTitle>
          <CardDescription>
            API sources can be checked from here. Bulk/file sources are surfaced here but imported
            from Data Ingress.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading connections...
            </p>
          ) : null}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Source</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Cadence</TableHead>
                  <TableHead>Latest check</TableHead>
                  <TableHead>Docs</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!isLoading && sources.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      No data sources registered yet. Apply the valuation data-source migration.
                    </TableCell>
                  </TableRow>
                ) : null}
                {sources.map((source) => (
                  <TableRow key={source.id}>
                    <TableCell className="min-w-[260px]">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{source.name}</span>
                          {source.auth_required ? (
                            <KeyRound className="h-3.5 w-3.5 text-muted-foreground" />
                          ) : null}
                        </div>
                        <p className="text-xs text-muted-foreground">{source.description}</p>
                        <p className="text-xs text-muted-foreground">
                          {source.provider ?? "Internal"} · {formatLabel(source.spatial_resolution)} ·{" "}
                          {formatLabel(source.temporal_resolution)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        <Badge variant="secondary">{formatLabel(source.category)}</Badge>
                        <Badge variant="outline">{formatLabel(source.access_mode)}</Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={STATUS_VARIANTS[source.status] ?? "secondary"}>
                        {formatLabel(source.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>{source.cadence ?? "—"}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Badge
                          variant={
                            source.latest_check_status
                              ? CHECK_VARIANTS[source.latest_check_status]
                              : "secondary"
                          }
                        >
                          {source.latest_check_status
                            ? formatLabel(source.latest_check_status)
                            : "not checked"}
                        </Badge>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(source.latest_check_at)}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {source.documentation_url || source.homepage_url ? (
                        <a
                          href={source.documentation_url ?? source.homepage_url ?? undefined}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                        >
                          Open
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!source.health_url || checkMutation.isPending}
                        onClick={() => checkMutation.mutate(source.slug)}
                      >
                        {checkMutation.isPending &&
                        checkMutation.variables === source.slug ? (
                          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                        ) : source.latest_check_status === "ok" ? (
                          <CheckCircle2 className="mr-2 h-3.5 w-3.5" />
                        ) : (
                          <RefreshCw className="mr-2 h-3.5 w-3.5" />
                        )}
                        Check
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DataConnections;
